import { ROUTES, UserJourneyValues } from "constants/index";
import { useCallback, useEffect } from "react";
import { useRouter } from "next/compat/router";
import { useIsV2InApp } from "./useIsV2InApp";
import { useNavigateToDigitalMenu } from "./useNavigateToDigitalMenu";
import { closeWebview } from "../utils/nativeBridgeHelper";

declare global {
    interface Window {
        handleHardwareBack?: () => void;
    }
}

interface BackHandlerConfig {
    [key: string]: () => boolean;
}

interface PnNavigation {
    navigateToMenu: () => boolean;
    isPnWebviewBack: boolean;
    userJourney: string;
}

// Define back navigation behavior
const getBackHandlerConfig = (
    router: ReturnType<typeof useRouter>,
    isV2InApp: boolean,
    pnNav: PnNavigation,
): BackHandlerConfig => ({
    TRACK: () => {
        router?.replace(ROUTES.MENU); // Use replace to prevent history stack issues
        return true;
    },
    MENU: () => {
        // do nothing
        return true;
    },
    COLLECTIONS: () => {
        router?.replace(ROUTES.MENU);
        return true;
    },
    BILL_SUMMARY: () => {
        if (isV2InApp) {
            if (pnNav.userJourney === UserJourneyValues.LIVE_NOTIFICATION || !pnNav.navigateToMenu()) {
                closeWebview();
            }
        } else {
            router?.replace(ROUTES.MENU);
        }
        return true;
    },
    BACK: () => {
        router?.back();
        return true;
    },
    CART_ORDERS: () => {
        if (isV2InApp) {
            if (pnNav.userJourney === UserJourneyValues.LIVE_NOTIFICATION || !pnNav.navigateToMenu()) {
                closeWebview();
            }
        } else if (pnNav.isPnWebviewBack) {
            router?.replace(ROUTES.MENU);
        } else {
            router?.back();
        }
        return true;
    },
    PAYMENT: () => {
        router?.replace(ROUTES.BILL_SUMMARY);
        return true;
    },
});

export const useHardwareBackHandler = (currentPage: string): void => {
    const router = useRouter();
    const { isV2InApp } = useIsV2InApp();
    const pnNav = useNavigateToDigitalMenu();

    const executeBackHandler = useCallback(() => {
        const handler = getBackHandlerConfig(router, isV2InApp, pnNav)[currentPage];
        handler?.();
    }, [currentPage, router, isV2InApp, pnNav]);

    useEffect(() => {
        window.handleHardwareBack = executeBackHandler;

        if (isV2InApp && router?.isReady) {
            const currentQuery = router.query;
            if (!currentQuery.custom_back) {
                router.replace(
                    { pathname: router.pathname, query: { ...currentQuery, custom_back: "true" } },
                    undefined,
                    { shallow: true },
                );
            }
        }

        const handleBackPress = (event: PopStateEvent): void => {
            const handler = getBackHandlerConfig(router, isV2InApp, pnNav)[currentPage];
            if (handler) {
                const shouldPreventDefault = handler();
                if (shouldPreventDefault) {
                    event.preventDefault();
                    window.history.pushState(null, "", window.location.href); // Keeps the user on the same page
                }
            }
        };

        // Push initial state to prevent default browser back behavior
        window.history.pushState(null, "", window.location.href);
        window.addEventListener("popstate", handleBackPress);

        return () => {
            window.removeEventListener("popstate", handleBackPress);
            window.handleHardwareBack = () => void 0;
        };
    }, [currentPage, router, isV2InApp, pnNav, executeBackHandler]);
};
